import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BrasolMark } from "./BrasolMark";
import { FkIcon, type FkIconName } from "./Icons";
import { CRM_URL } from "./AccountMenu";

export interface NavItem {
  /** Внутренний путь SMS-центра; у `external` — полный адрес другого сервиса (CRM). */
  to: string;
  label: string;
  /** Пункт ведёт в другой сервис (CRM БРАСОЛ): открывается в новой вкладке, меню не подсвечивает. */
  external?: boolean;
  icon?: FkIconName;
  badge?: number;
  /** Устарело: совпадение и так только точное или по matchPrefixes/matchPattern. */
  end?: boolean;
  /** Префиксы, при которых пункт активен (например, /campaigns для редактора). */
  matchPrefixes?: string[];
  /** Дополнительный регэксп активности (результат кампании). */
  matchPattern?: RegExp;
  /** Префиксы-исключения (результаты подсвечивают другой пункт). */
  excludePrefixes?: string[];
  excludePattern?: RegExp;
}

export interface NavGroup {
  key:
    | "campaigns"
    | "finance"
    | "settings"
    | "content"
    | "analytics"
    | "data"
    | "workspace";
  label: string;
  icon: FkIconName;
  items: NavItem[];
  defaultOpen?: boolean;
}

export function navItemActive(item: NavItem, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (
    item.excludePrefixes?.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    )
  )
    return false;
  if (item.excludePattern?.test(path)) return false;
  if (path === item.to) return true;
  // «Настройки» (end) подсвечиваются и на «Каналах отправки» (matchPrefixes) — как в прототипе.
  if (item.matchPattern?.test(path)) return true;
  return !!item.matchPrefixes?.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** Группа, в которой лежит активный пункт; страница вне меню — null. */
export function activeGroupKey(
  groups: NavGroup[],
  pathname: string,
): NavGroup["key"] | null {
  return (
    groups.find((group) =>
      group.items.some((item) => navItemActive(item, pathname)),
    )?.key ?? null
  );
}

/** Широкий экран: в свёрнутом меню щелчок по группе разворачивает меню. На телефоне меню всегда полное. */
function isDesktop(): boolean {
  try {
    return window.matchMedia("(min-width: 900px)").matches;
  } catch {
    return true;
  }
}

/**
 * Боковое меню каркаса CRM v2: бренд «БРАСОЛ · CRM-маркетинг» (в свёрнутом виде — знак «Б»),
 * кружок «свернуть/развернуть» на стыке с шапкой, «В CRM БРАСОЛ», группы-аккордеон
 * (открыта одна — по умолчанию группа текущего экрана), подвал с обучением.
 * Ширина 240 / 64 задаётся классом `.fk-compact` оболочки; на <900 px меню выезжает
 * целиком и свёрнутым не бывает — поэтому обе версии бренда и подписи рендерятся
 * всегда, а прячет их CSS.
 */
export function Sidebar({
  groups,
  compact = false,
  onToggleCompact,
  onStartTour,
  tourDone,
  onNavigate,
  focusFirst,
}: {
  groups: NavGroup[];
  compact?: boolean;
  onToggleCompact?: () => void;
  onStartTour: () => void;
  tourDone: boolean;
  /** Клик по пункту (закрыть мобильное меню). */
  onNavigate?: () => void;
  /** После открытия мобильного меню — фокус на первый пункт. */
  focusFirst?: boolean;
}) {
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  const active = activeGroupKey(groups, pathname);
  const [openKey, setOpenKey] = useState<string>(
    () => active ?? groups[0]?.key ?? "",
  );

  // Переход в другую группу (ссылкой со страницы, тур) раскрывает её.
  useEffect(() => {
    if (active) setOpenKey(active);
  }, [active]);

  useEffect(() => {
    if (focusFirst)
      navRef.current?.querySelector<HTMLElement>("a, button")?.focus();
  }, [focusFirst]);

  const onGroup = (key: string) => {
    if (compact && isDesktop()) {
      setOpenKey(key);
      onToggleCompact?.();
      return;
    }
    setOpenKey((current) => (current === key ? "" : key));
  };

  const collapseLabel = compact ? "Развернуть меню" : "Свернуть меню";
  return (
    <aside id="fk-sidebar" className="fk-sidebar" aria-label="Боковое меню">
      <div className="fk-brand">
        <Link
          to="/campaign-dashboard"
          className="fk-brand-link"
          aria-label="БРАСОЛ · CRM-маркетинг — обзор"
          title="БРАСОЛ · CRM-маркетинг"
          onClick={onNavigate}
        >
          <BrasolMark size={36} />
          <span className="fk-brand-full">
            <img
              className="fk-brand-fish"
              src="/brand/fishka-fish-only.png"
              alt=""
            />
            <span className="fk-brand-text">
              <span className="fk-brand-name">БРАСОЛ</span>
              <span className="fk-brand-sub">CRM-маркетинг</span>
            </span>
          </span>
        </Link>
        {onToggleCompact && (
          <button
            type="button"
            className="fk-collapse"
            onClick={onToggleCompact}
            aria-label={collapseLabel}
            title={collapseLabel}
          >
            <FkIcon name={compact ? "chevR" : "chevL"} size={14} />
          </button>
        )}
      </div>
      <div className="fk-crm-back">
        <a
          className="fk-crm-link"
          href={CRM_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="CRM БРАСОЛ — отдельный сервис, откроется в новой вкладке"
        >
          <FkIcon name="back" size={14} />
          <span>В CRM БРАСОЛ</span>
          <span className="fk-crm-link-ext" aria-hidden="true">
            ↗
          </span>
        </a>
      </div>
      <nav ref={navRef} aria-label="Главное меню">
        {groups.map((group) => {
          const open = openKey === group.key;
          const isActive = active === group.key;
          return (
            <div
              className={`fk-nav-group${isActive ? " is-active" : ""}${open ? " is-open" : ""}`}
              key={group.key}
            >
              <button
                type="button"
                className="fk-nav-group-btn"
                aria-expanded={open}
                aria-controls={open ? `fk-nav-${group.key}` : undefined}
                title={group.label}
                onClick={() => onGroup(group.key)}
              >
                <FkIcon name={group.icon} size={18} />
                <span className="fk-nav-group-label">{group.label}</span>
                <FkIcon
                  name={open ? "chevU" : "chevD"}
                  size={14}
                  className="fk-nav-chev"
                />
              </button>
              {open && (
                <div className="fk-nav-items" id={`fk-nav-${group.key}`}>
                  {group.items.map((item) => {
                    if (item.external) {
                      return (
                        <a
                          key={item.to}
                          href={item.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`${item.label} — страница CRM БРАСОЛ, откроется в новой вкладке`}
                          className="fk-nav-item"
                          onClick={onNavigate}
                        >
                          <span>{item.label}</span>
                          <span className="fk-crm-link-ext" aria-hidden="true">
                            ↗
                          </span>
                        </a>
                      );
                    }
                    const current = navItemActive(item, pathname);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        title={item.label}
                        className={`fk-nav-item${current ? " active" : ""}`}
                        aria-current={current ? "page" : undefined}
                        onClick={onNavigate}
                      >
                        <span>{item.label}</span>
                        {!!item.badge && (
                          <span className="fk-nav-badge">{item.badge}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="fk-sidebar-foot">
        <button
          type="button"
          className="fk-tour-btn"
          title="Обучение: как устроен SMS-центр"
          aria-label={tourDone ? "Пройти обучение заново" : "Пройти обучение"}
          onClick={onStartTour}
        >
          <FkIcon name="bulb" size={16} />
          <span>{tourDone ? "Пройти обучение заново" : "Пройти обучение"}</span>
        </button>
        <div className="fk-sidebar-caption">
          CRM-маркетинг fishka · sms.brasol.ru
          <br />© BRASOL
        </div>
      </div>
    </aside>
  );
}
